import { CONTENT_WIDE_BREAKPOINT } from './breakpoints';

/** Fixed cart column width on wide POS (web / tablet landscape). */
/** Proto desk cart panel (~340px). */
export const POS_CART_WIDTH_WIDE = 340;

/** Minimum product card width used to derive column count. */
export const POS_GRID_MIN_CARD_WIDTH = 160;

/**
 * Vertical stride per grid row on desk cards (`minHeight` 76 + `marginBottom` 8).
 * Keep in sync with `PosProductGridItem` desk card styles.
 */
export const POS_GRID_ROW_STRIDE_PX = 84;

/** Desk catalog list content padding (top 12 + bottom sm). */
export const POS_CATALOG_LIST_PADDING_V_PX = 20;

/** Clamp viewport-derived rows so pages stay usable on tiny / huge panes. */
export const POS_CATALOG_VIEWPORT_ROWS_MIN = 3;
export const POS_CATALOG_VIEWPORT_ROWS_MAX = 8;

/**
 * Product grid columns from available catalog width (after cart + padding).
 * @param catalogWidth — inner width of catalog column in px
 */
export function getPosGridColumns(catalogWidth: number, layoutWide: boolean): number {
  if (!layoutWide || catalogWidth < CONTENT_WIDE_BREAKPOINT - POS_CART_WIDTH_WIDE) {
    return 2;
  }
  const cols = Math.floor(catalogWidth / POS_GRID_MIN_CARD_WIDTH);
  return Math.max(2, Math.min(6, cols));
}

/** Dos tamaños de página alineados a filas completas de la grilla (2 y 3 filas). Mobile / fallback. */
export function getPosCatalogPageSizeOptions(numColumns: number): {
  options: readonly [number, number];
  defaultSize: number;
} {
  const cols = Math.max(1, numColumns);
  if (cols === 1) {
    return { options: [8, 12], defaultSize: 12 };
  }
  const smaller = cols * 2;
  const larger = cols * 3;
  return { options: [smaller, larger], defaultSize: larger };
}

/**
 * Page size that fills the measured catalog list viewport with complete grid rows.
 * `listViewportHeight` = FlatList layout height; `listHeaderHeight` = ListHeader (desk toolbar).
 */
export function getPosCatalogViewportPageSize(
  numColumns: number,
  listViewportHeight: number,
  listHeaderHeight = 0,
  options?: {
    rowStridePx?: number;
    paddingVPx?: number;
    minRows?: number;
    maxRows?: number;
  }
): number {
  const cols = Math.max(1, numColumns);
  const rowStride = options?.rowStridePx ?? POS_GRID_ROW_STRIDE_PX;
  const paddingV = options?.paddingVPx ?? POS_CATALOG_LIST_PADDING_V_PX;
  const minRows = options?.minRows ?? POS_CATALOG_VIEWPORT_ROWS_MIN;
  const maxRows = options?.maxRows ?? POS_CATALOG_VIEWPORT_ROWS_MAX;

  const usable = listViewportHeight - Math.max(0, listHeaderHeight) - paddingV;
  if (!(usable > 0) || !(rowStride > 0)) {
    return cols * minRows;
  }
  const rawRows = Math.floor(usable / rowStride);
  const rows = Math.max(minRows, Math.min(maxRows, rawRows));
  return cols * rows;
}

export function formatPosCatalogPageSizeLabel(size: number, numColumns: number): string {
  if (numColumns <= 1) {
    return size === 1 ? '1 ítem' : `${size} ítems`;
  }
  const rows = size / numColumns;
  return rows === 1 ? '1 fila' : `${rows} filas`;
}
